import { useState } from 'react'
import { Beaker, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react'
import { MetricCard } from '../primitives'

interface Method {
  method_specification: string
  category: string
  subcategory?: string
  indicator: string
  unit: string
  description?: string
}

interface SearchMethodsData {
  methods: Method[]
  total_found: number
  query_keywords?: string[]
  database?: string
  success?: boolean
}

interface SearchMethodsRendererProps {
  data: SearchMethodsData
}

function MethodCard({ method }: { method: Method }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(method.method_specification)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      console.error('Failed to copy:', e)
    }
  }

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
      {/* Category & Indicator */}
      <div className="flex items-start gap-2">
        <Beaker className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-tight">{method.indicator}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {method.category}
            {method.subcategory && method.subcategory !== method.category && (
              <> &bull; {method.subcategory}</>
            )}
          </div>
        </div>
        {/* Unit badge */}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium whitespace-nowrap">
          {method.unit}
        </span>
      </div>

      {/* Method specification (copyable) */}
      <div className="flex items-center gap-1 pl-[22px]">
        <code className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded flex-1 truncate">
          {method.method_specification}
        </code>
        <button
          onClick={handleCopy}
          className="p-1 hover:bg-muted rounded transition-colors flex-shrink-0"
          title="Copy method specification"
        >
          {copied ? (
            <Check className="w-3 h-3 text-success" />
          ) : (
            <Copy className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      </div>

      {/* Description if present */}
      {method.description && (
        <p className="text-xs text-muted-foreground pl-[22px] line-clamp-2">{method.description}</p>
      )}
    </div>
  )
}

export function SearchMethodsRenderer({ data }: SearchMethodsRendererProps) {
  const [showAll, setShowAll] = useState(false)

  const methods = data.methods || []
  const maxVisible = 3
  const visibleMethods = showAll ? methods : methods.slice(0, maxVisible)
  const hasMore = methods.length > maxVisible

  return (
    <div className="space-y-3">
      {/* Summary metrics */}
      <div className="flex flex-wrap gap-2">
        <MetricCard
          label="Methods Found"
          value={data.total_found}
          variant={data.total_found > 0 ? 'success' : 'warning'}
          size="sm"
        />
        {data.database && (
          <MetricCard
            label="Database"
            value={data.database.replace('ecoinvent-', '').replace('-cutoff', '')}
            size="sm"
          />
        )}
      </div>

      {/* Query keywords */}
      {data.query_keywords && data.query_keywords.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Keywords:{' '}
          {data.query_keywords.map((kw, i) => (
            <span key={i}>
              {i > 0 && ', '}"<span className="font-medium">{kw}</span>"
            </span>
          ))}
        </div>
      )}

      {/* Methods list */}
      {methods.length > 0 ? (
        <div className="space-y-2">
          {visibleMethods.map((method, i) => (
            <MethodCard key={method.method_specification || i} method={method} />
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-2">
          No methods found. Try different keywords.
        </div>
      )}

      {/* Show more/less */}
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {showAll ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show {methods.length - maxVisible} more
            </>
          )}
        </button>
      )}
    </div>
  )
}
